// How Fleet names what a session was, in the order a session flows: an agent
// calls a model, served by a provider or by the endpoint itself (local
// inference, a runtime). As the backend records them:
//
//   Agents    what a session was, with as much fidelity as the record holds:
//             the client surface its trace names (claude-code, copilot-vscode)
//             where that names a product, else the agent type its frames
//             report (a product name such as "Hermes", or a legacy type), else
//             its provider, but Claude Code where Claude Code's own captures
//             caught an Anthropic trace. How it was captured (hooks, OTLP, a
//             transcript, the local tap) is never an agent's name: a trace
//             whose surface is only its capture path is named by its frames
//             or provider instead, or by the one product that path carries
//             (Origin's own chat, Claude in Office).
//             Installed copies are the endpoint inventory's products of the
//             same name.
//   Models    the model a session's frames name, one however it is written.
//   Runtimes  the local runtime a launch or GPU inference names. A local
//             session is stamped provider "local" and does not name the
//             runtime that served it.
//   Marks     the tenant's mark on a provider: sanctioned, unsanctioned (the
//             default) or ignored, set in Settings.
//
// Pure: no network, no React. The reads are lib/fleetConsoleReads.js, the
// split lib/fleetBreakdown.js.
import { agentDisplayLabel, clientSurfaceIdentity, deriveAgentIdentity, humanizeAgentName, slugifyAgentName } from "./agentIdentity.js";
import products from "../../../shared-protos/catalogs/software-inventory-products.json";

// The inventory's product, by the name an endpoint reported (its display name
// or the legacy key older agents still send), from the frozen registry.
const CATALOG = products.map(({ legacy_key, product }) => ({ ...product, legacy_key }));
const inventoryIdentity = (name) => CATALOG.find((p) => p.display_name === name || p.legacy_key === name) || null;

// Each list's name.
export const VIEW_LABEL = { agents: "Agents", models: "Models", runtimes: "Local inference" };

// The past 30 days, which the endpoints table counts sessions over.
export const FLEET_RANGE = "1M";
export const FLEET_STALE_MS = 5 * 60 * 1000;

// The capture sources an endpoint's own agent writes. Every other source is a
// connector, listed by its own name.
export const ENDPOINT_SOURCES = ["proxy", "otlp", "office", "hooks", "transcript", "local_tap", "browser_extension", "otlp_traces"];
const SOURCE_NUMBER = { proxy: 1, otlp: 2, office: 3, compliance_sync: 4, hooks: 5, transcript: 6, copilot_sync: 7, openai_sync: 8, dashboard_chat: 9, local_tap: 10, perplexity_audit_log: 11, browser_extension: 12, langsmith_sync: 13, otlp_traces: 14, vertex_sync: 15, litellm_sync: 16 };
const NUMBER_SOURCE = Object.fromEntries(Object.entries(SOURCE_NUMBER).map(([k, v]) => [v, k]));

// A stored capture value as its source's name ("hooks", "vertex_sync"), ""
// when none is stored.
export function captureName(raw) {
  const s = String(raw ?? "").trim().toLowerCase().replace(/^capture_source_/, "");
  const name = /^\d+$/.test(s) ? NUMBER_SOURCE[Number(s)] || "" : s === "otlp_logs" ? "otlp" : s;
  return !name || name === "0" || name === "unspecified" ? "" : name;
}

// Every stored form of a source's capture values, for a filter to match: its
// name, its enum name and its number in text (newer sources are stored as
// their number).
export function sourceValues(id) {
  const names = id === "endpoints" ? ENDPOINT_SOURCES : [id];
  return names.flatMap((n) => [n, `capture_source_${n}`, ...(SOURCE_NUMBER[n] ? [String(SOURCE_NUMBER[n])] : [])]);
}

// ---- agents ---------------------------------------------------------------

// Values a trace's client surface can hold that name how it was captured, not
// what produced it: the capture paths, written either way, and the empty
// "unattributed" surface.
const CAPTURE_WORDS = [...Object.keys(SOURCE_NUMBER), "otlp_logs", "office_traces", "host_artifact", "analytics_api", "sync_only_metadata", "unknown", "unspecified"];
export const CAPTURE_SURFACES = ["", ...new Set(CAPTURE_WORDS.flatMap((w) => [w, w.replace(/_/g, "-")]))];
const CAPTURE_SET = new Set(CAPTURE_SURFACES);
const norm = (v) => String(v ?? "").trim().toLowerCase();
const NO_TYPE = /^(|0|unspecified|ai_agent_type_unspecified|unknown)$/i;
export const hasAgentType = (agentType) => !NO_TYPE.test(String(agentType ?? "").trim());

// Surfaces whose written-out form misreads the product or spells it a second
// way, and the capture paths that carry one product whatever its provider:
// Origin's own chat, and Anthropic's agent in Office. Looked up with hyphens.
const KNOWN_SURFACES = { chatgpt: "ChatGPT", "chatgpt-web": "ChatGPT", "claude-code": "Claude Code", "codex-cli": "Codex CLI", "gemini-cli": "Gemini CLI", "m365-copilot": "Microsoft 365 Copilot", "copilot-sdk": "GitHub Copilot SDK", "origin-dashboard": "Origin chat", "dashboard-chat": "Origin chat", "office-traces": "Claude in Office" };
function surfaceName(surface) {
  const known = clientSurfaceIdentity(surface);
  if (known) return known.surface ? `${known.name} (${known.surface})` : known.name;
  return humanizeAgentName(surface);
}
// What Claude Code's own captures write on an endpoint: its OTLP logs, hooks
// and transcripts (Cowork's too, Claude Code inside Claude Desktop). The
// compliance feed, the browser and the proxy reach any Claude app.
const CLAUDE_CODE_CAPTURES = ["otlp", "hooks", "transcript"];

/**
 * The agent one group of traces was: { key, name, by } where `by` says which
 * part of the record named it ("surface", "type" or "provider"). Keys are the
 * slug of the name, so the same product reached three ways ("claude-code" as a
 * surface, "Claude Code" as an agent type) is one agent.
 */
export function resolveAgent({ surface = "", provider = "", agentType = "" } = {}) {
  const s = norm(surface);
  const product = KNOWN_SURFACES[s.replace(/_/g, "-")];
  if (product) return { key: slugifyAgentName(product), name: product, by: "surface" };
  if (s && !CAPTURE_SET.has(s)) return { key: s, name: surfaceName(s), by: "surface" };
  const p = norm(provider);
  const known = deriveAgentIdentity("", "", "", p);
  // The name the provider alone gives ("Claude" for anthropic).
  const plain = known.key === "unknown" ? humanizeAgentName(p) : known.name;
  const type = String(agentType ?? "").trim();
  if (hasAgentType(type)) {
    // A legacy type is humanized; anything else is the product's own name.
    const name = /^\d+$/.test(type) ? deriveAgentIdentity(Number(type)).name : agentDisplayLabel(type);
    const key = slugifyAgentName(name);
    // One that only repeats the provider (a legacy "Claude") names no more than it.
    if (key && (!p || key !== slugifyAgentName(plain))) return { key, name, by: "type" };
  }
  // A local capture's provider names the loopback, not the client.
  if (p === "local") return { key: "local-client", name: "Unidentified local client", by: "provider" };
  if (p === "anthropic" && CLAUDE_CODE_CAPTURES.includes(captureName(s.replace(/-/g, "_")))) return { key: "claude-code", name: "Claude Code", by: "provider" };
  if (p) return { key: slugifyAgentName(plain), name: plain, by: "provider" };
  return { key: "unattributed", name: "Unattributed", by: "provider" };
}

// One trace group (surface, provider, agent type), as a key.
export const groupKey = (g) => [norm(g.surface), norm(g.provider), String(g.agentType ?? "").trim()].join("\u0000");

/**
 * Trace groups (surface, provider, agent type) folded into agents. Each agent
 * keeps what matches it, for a read to count it exactly: the product surfaces
 * that name it, and, per provider, the capture-only surfaces and agent types
 * that did, and the groups themselves (`groups`, by groupKey): an agent of one
 * group is counted exactly by it. `sessions` here sums the groups, an estimate
 * until the exact count.
 */
export function identifyAgents(groups = []) {
  const out = new Map();
  for (const g of groups) {
    const id = resolveAgent(g);
    if (!out.has(id.key)) out.set(id.key, { key: id.key, name: id.name, surfaces: new Set(), byProvider: new Map(), providers: new Set(), groups: new Set(), sessions: 0, lastAt: null });
    const a = out.get(id.key);
    a.groups.add(groupKey(g));
    const provider = norm(g.provider);
    if (provider) a.providers.add(provider);
    if (id.by === "surface") a.surfaces.add(norm(g.surface));
    else {
      if (!a.byProvider.has(provider)) a.byProvider.set(provider, { surfaces: new Set(), types: new Set(), untyped: false });
      const m = a.byProvider.get(provider);
      m.surfaces.add(norm(g.surface));
      // A type its provider named over (a legacy "Claude") still matches it.
      if (hasAgentType(g.agentType)) m.types.add(String(g.agentType).trim());
      else m.untyped = true;
    }
    a.sessions += Number(g.sessions) || 0;
    const last = num(g.last);
    if (last != null) a.lastAt = Math.max(a.lastAt ?? 0, last);
  }
  return out;
}

// What an agent matches, as plain data a read can carry: product surfaces,
// and per provider the capture-only surfaces and agent types (`untyped` when
// traces with no agent type were named by the provider).
export function agentMatch(a) {
  return {
    surfaces: [...a.surfaces],
    providers: [...a.byProvider].map(([provider, m]) => ({ provider, surfaces: [...m.surfaces], types: [...m.types], untyped: m.untyped })),
  };
}

// The key an installed product is joined on: the slug of its name, which is
// how a surface or an agent type is keyed ("Claude Code" is claude-code).
export const installedKey = (name) => slugifyAgentName(inventoryIdentity(name)?.display_name || String(name ?? ""));
export const isRuntimeProduct = (name) => inventoryIdentity(name)?.category === "local_runtime";
export const RUNTIME_PRODUCT_NAMES = CATALOG.filter((p) => p.category === "local_runtime").flatMap((p) => [p.display_name, p.legacy_key]).filter(Boolean);

const num = (v) => (v == null || v === "" ? null : Number.isFinite(Number(v)) ? Number(v) : null);

/**
 * The agents list.
 *   agents:    identifyAgents' map
 *   exact:     Map<key, { sessions, machines, then?, now? }> counted exactly,
 *              with the first and last week's { sessions, machines }, or null
 *   installed: [{ name, provider, installed }] or null when unread
 *   links:     Map<key, number> of models each agent called, or null
 * Local runtimes' products are left for the runtimes list.
 */
export function mergeAgents({ agents = new Map(), exact = null, installed = null, links = null } = {}) {
  const rows = new Map();
  for (const a of agents.values()) {
    const e = exact?.get(a.key);
    rows.set(a.key, {
      id: a.key, kind: "agents", name: a.name, match: agentMatch(a),
      providers: [...a.providers], provider: [...a.providers][0] || "",
      sessions: e ? e.sessions : a.sessions, machines: e ? e.machines : null, estimated: !e,
      week: e?.then && e?.now ? { then: e.then, now: e.now } : null,
      models: links ? links.get(a.key) ?? 0 : null,
      installed: installed ? 0 : null, inventoryNames: [],
      lastAt: a.lastAt,
    });
  }
  // An installed product joins the agent keyed by its name, or whose surface
  // the identity table names it ("copilot-vscode" is GitHub Copilot).
  const alias = new Map();
  for (const key of rows.keys()) {
    alias.set(key, key);
    const known = clientSurfaceIdentity(key);
    if (known && !alias.has(slugifyAgentName(known.name))) alias.set(slugifyAgentName(known.name), key);
  }
  for (const i of installed || []) {
    if (isRuntimeProduct(i.name)) continue;
    const slug = installedKey(i.name);
    if (!slug) continue;
    const key = alias.get(slug) || slug;
    if (!rows.has(key)) {
      const name = inventoryIdentity(i.name)?.display_name || String(i.name);
      rows.set(key, { id: key, kind: "agents", name, match: { surfaces: [], providers: [] }, providers: [], provider: String(i.provider || "").toLowerCase(), sessions: 0, machines: 0, estimated: false, week: null, models: links ? 0 : null, installed: 0, inventoryNames: [], lastAt: null });
    }
    const r = rows.get(key);
    r.installed = (r.installed || 0) + (Number(i.installed) || 0);
    r.inventoryNames.push(String(i.name));
    if (!r.provider && i.provider) r.provider = String(i.provider).toLowerCase();
  }
  return [...rows.values()];
}

// ---- runtimes -------------------------------------------------------------

// The runtime a launch or GPU inference names: the inventory product its
// host runtime (the nearest recognized ancestor process), process image or tag
// is, else whichever of those names more than the generic "local" tag.
export function runtimeOf({ tag = "", image = "", host = "" } = {}) {
  const baseOf = (v) => String(v || "").split(/[\\/]/).pop().replace(/\.(exe|app)$/i, "");
  const base = baseOf(image);
  const hostBase = baseOf(host);
  const bySlug = (v) => { const sl = slugifyAgentName(v); return sl ? CATALOG.find((p) => p.category === "local_runtime" && (slugifyAgentName(p.display_name) === sl || slugifyAgentName(p.legacy_key) === sl || slugifyAgentName(p.display_name).replace(/-/g, "") === sl.replace(/-/g, ""))) : null; };
  const product = bySlug(hostBase) || bySlug(base) || bySlug(tag);
  if (product) return { key: slugifyAgentName(product.display_name), name: product.display_name };
  const word = (tag && tag !== "local" ? tag : "") || hostBase || base || tag;
  return word ? { key: slugifyAgentName(word), name: humanizeAgentName(word) } : null;
}

// ---- models ---------------------------------------------------------------

// A model is one model however it is written: in lower case, without a
// route's prefix ("openai/", Bedrock's "us.anthropic."), its words in any
// order and its numbers run together, so "claude-sonnet-5-5", "Claude Sonnet
// 5.5" and "claude55sonnet" are one. A size, tag or suffix ("qwen3:14b",
// "-instruct", "-20251001") is part of its name and keeps models apart.
const ROUTE = /^(?:[a-z]{2}\.)?(?:anthropic|amazon|meta|mistral|cohere|ai21|deepseek|openai|qwen|writer)\./;
export function modelKey(model) {
  const s = String(model ?? "").toLowerCase().replace(/^.*[\\/]/, "").replace(ROUTE, "");
  return `${(s.match(/[a-z]+/g) || []).sort().join(" ")}|${(s.match(/\d+/g) || []).join("")}`;
}

// Ollama's own runner reports the model as its file in Ollama's blob store
// (".../.ollama/models/blobs/sha256-<digest>"), where the tap misses the leg
// that names it. Such a model is named by its digest; any other path by its file.
export const isModelBlob = (model) => /[\\/]sha256-[0-9a-f]{8,}$/i.test(String(model ?? ""));
export function modelLabel(model) {
  const file = String(model ?? "").split(/[\\/]/).pop();
  const digest = /^sha256-([0-9a-f]{12})/i.exec(file);
  return digest ? `Ollama model ${digest[1]}` : file;
}

// ---- providers ------------------------------------------------------------

export const MARKS = ["sanctioned", "unsanctioned", "ignored"];
export const MARK_LABEL = { sanctioned: "Sanctioned", unsanctioned: "Unsanctioned", ignored: "Ignored" };
const MARK_OF = { AI_SERVICE_DISPOSITION_SANCTIONED: "sanctioned", AI_SERVICE_DISPOSITION_IGNORED: "ignored" };
export const markOf = (disposition) => MARK_OF[disposition] || "unsanctioned";
export const dispositionOf = (mark) => `AI_SERVICE_DISPOSITION_${String(mark).toUpperCase()}`;

/**
 * The providers list: each provider the tenant's policy names, with its mark
 * and the machines that reached it.
 *   policies: ListAiServicePolicies' policies, in either JSON spelling
 *   reached:  Map<provider tag, machines>, or null when unread
 * The server removes an ignored provider's evidence, so its machines are
 * unknown (null), never none.
 */
export function mergeProviders({ policies = [], reached = null } = {}) {
  return policies.map((p) => {
    const tag = String(p.providerTag ?? p.provider_tag ?? "");
    const mark = markOf(p.disposition);
    return { id: tag, kind: "providers", tag, name: String(p.displayName || p.display_name || tag), mark, machines: mark === "ignored" || !reached ? null : reached.get(tag) ?? 0 };
  });
}
